import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Mail, CheckCircle2, AlertTriangle } from 'lucide-react';
import PageHeader from '../../components/PageHeader';
import Button from '../../components/ui/Button';
import nurseryAccountsService from '../../services/nurseryAccounts';
import { useModuleLicense } from '../../hooks/useModuleLicense';
import { errorMessage } from '../../utils/apiResponse';
import { money } from '../../utils/nurseryMoney';
import { useOrgTimezone, formatDateTimeInTz, formatLocalDateInTz } from '../../utils/timezone';
import type {
  StatementCandidate, StatementPreview, StatementSendResult, StatementSettings, StatementSkipReason,
} from '../../types/nurseryAccounts';

/**
 * /accounts/statements — monthly customer statements (backend
 * nurseryStatementRunService). Each customer that pays its own invoices is
 * emailed last month's statement as a PDF on the day and hour set here. Any
 * accounts seat reads; the Accounts Admin changes the settings and sends.
 */

const SKIP: Record<StatementSkipReason, string> = {
  no_statements: 'Statements off: its invoices are not emailed, or it said no',
  no_email: 'No email address',
  nothing: 'Nothing in the month and nothing owed',
};

const RESULT: Record<StatementSendResult['status'], string> = {
  sent: 'Sent', failed: 'Not sent', skipped: 'Skipped', already_sent: 'Already sent',
};

const ordinal = (n: number) => `${n}${n % 10 === 1 && n !== 11 ? 'st' : n % 10 === 2 && n !== 12 ? 'nd' : n % 10 === 3 && n !== 13 ? 'rd' : 'th'}`;
const hourText = (h: number) => `${h % 12 === 0 ? 12 : h % 12}:00 ${h < 12 ? 'am' : 'pm'}`;

function lastMonth(): string {
  const d = new Date();
  d.setDate(1);
  d.setMonth(d.getMonth() - 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

function SettingsCard({ settings, isAdmin, onSaved }: {
  settings: StatementSettings; isAdmin: boolean; onSaved: (s: StatementSettings) => void;
}) {
  const tz = useOrgTimezone();
  const [form, setForm] = useState(settings);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  useEffect(() => { setForm(settings); }, [settings]);

  const dirty = form.enabled !== settings.enabled || form.send_day !== settings.send_day
    || form.send_hour !== settings.send_hour || form.include_zero !== settings.include_zero
    || (form.message || '') !== (settings.message || '');
  const set = (patch: Partial<StatementSettings>) => { setForm({ ...form, ...patch }); setSaved(false); };

  const save = async () => {
    setBusy(true); setError(null);
    try {
      const res = await nurseryAccountsService.saveStatementSettings({
        enabled: form.enabled, send_day: form.send_day, send_hour: form.send_hour,
        include_zero: form.include_zero, message: form.message || null,
      });
      if (res.success && res.data) { onSaved(res.data); setSaved(true); }
      else setError(errorMessage(res, 'The settings could not be saved'));
    } catch (e) {
      setError(errorMessage(e, 'The settings could not be saved'));
    }
    setBusy(false);
  };

  const inputCls = 'rounded-lg border border-neutral-300 px-3 py-2 text-sm focus:border-primary-500 focus:ring-primary-500 disabled:bg-neutral-50';
  return (
    <fieldset disabled={!isAdmin || busy} className="bg-white border border-neutral-200 rounded-xl p-4 space-y-4 min-w-0">
      <legend className="sr-only">Statement settings</legend>
      <h2 className="text-base font-semibold text-neutral-900">Send them every month</h2>
      {error && <div className="bg-red-50 border border-red-200 rounded-lg px-3 py-2 text-sm text-red-700 break-words">{error}</div>}
      <label className="flex items-start gap-2 text-sm text-neutral-800">
        <input type="checkbox" className="mt-0.5" checked={form.enabled} onChange={(e) => set({ enabled: e.target.checked })} />
        <span className="break-words">Email each customer its statement for the month before</span>
      </label>
      <div className="flex flex-wrap items-end gap-3">
        <div className="space-y-1">
          <label htmlFor="stmt-day" className="block text-sm font-medium text-neutral-800">On the</label>
          <select id="stmt-day" className={inputCls} value={form.send_day} onChange={(e) => set({ send_day: Number(e.target.value) })}>
            {Array.from({ length: 28 }, (_, i) => i + 1).map((d) => <option key={d} value={d}>{ordinal(d)}</option>)}
          </select>
        </div>
        <div className="space-y-1">
          <label htmlFor="stmt-hour" className="block text-sm font-medium text-neutral-800">At</label>
          <select id="stmt-hour" className={inputCls} value={form.send_hour} onChange={(e) => set({ send_hour: Number(e.target.value) })}>
            {Array.from({ length: 24 }, (_, h) => h).map((h) => <option key={h} value={h}>{hourText(h)}</option>)}
          </select>
        </div>
        <p className="text-xs text-neutral-500 pb-2 break-words">Your organisation's time zone.</p>
      </div>
      <label className="flex items-start gap-2 text-sm text-neutral-800">
        <input type="checkbox" className="mt-0.5" checked={form.include_zero} onChange={(e) => set({ include_zero: e.target.checked })} />
        <span className="break-words">Also send to customers with nothing in the month and nothing owed</span>
      </label>
      <div className="space-y-1">
        <label htmlFor="stmt-message" className="block text-sm font-medium text-neutral-800">A note in the email (optional)</label>
        <textarea id="stmt-message" rows={3} maxLength={2000} className={`w-full ${inputCls}`}
          value={form.message || ''} placeholder="e.g. Please pay by EFT to the account on your invoice."
          onChange={(e) => set({ message: e.target.value })} />
      </div>
      {isAdmin && (
        <div className="flex flex-wrap items-center gap-3">
          <Button onClick={save} disabled={!dirty || busy}>{busy ? 'Saving…' : 'Save'}</Button>
          {saved && !dirty && <span className="flex items-center gap-1 text-sm text-green-800"><CheckCircle2 className="h-4 w-4" /> Saved</span>}
        </div>
      )}
      {settings.last_run_at && settings.last_run_period_to && (
        <p className="text-xs text-neutral-500 break-words">
          Last run {formatDateTimeInTz(settings.last_run_at, tz)}, for the month to {formatLocalDateInTz(settings.last_run_period_to, tz)}.
        </p>
      )}
    </fieldset>
  );
}

function CustomerRow({ c, checked, onCheck, canPick, result }: {
  c: StatementCandidate; checked: boolean; onCheck: (v: boolean) => void; canPick: boolean; result?: StatementSendResult;
}) {
  const tz = useOrgTimezone();
  const last = c.last_send;
  return (
    <li className="py-3 flex gap-3 min-w-0">
      {canPick && (
        <input type="checkbox" className="mt-1 shrink-0" checked={checked} aria-label={`Choose ${c.name}`}
          onChange={(e) => onCheck(e.target.checked)} />
      )}
      <div className="min-w-0 flex-1 space-y-0.5">
        <div className="flex flex-wrap items-baseline justify-between gap-x-3">
          <Link to={`/accounts/customers/${c.customer_id}`} className="font-medium text-primary-700 hover:underline break-words min-w-0">
            {c.name}{c.customer_code ? ` #${c.customer_code}` : ''}
          </Link>
          <span className="text-sm text-neutral-900 whitespace-nowrap">
            {money(c.closing_balance)}
            {c.overdue > 0 && <span className="text-red-700"> ({money(c.overdue)} overdue)</span>}
          </span>
        </div>
        <p className="text-xs text-neutral-500 break-words">
          {c.send ? `To ${c.email}` : SKIP[c.reason as StatementSkipReason]}
          {' · '}{c.lines} {c.lines === 1 ? 'entry' : 'entries'} in the month
        </p>
        {result ? (
          <p className={`text-xs break-words ${result.status === 'failed' ? 'text-red-700' : 'text-neutral-700'}`}>
            {RESULT[result.status]}{result.error ? `: ${result.error}` : ''}
          </p>
        ) : last ? (
          <p className={`text-xs break-words ${last.status === 'failed' ? 'text-red-700' : 'text-green-800'}`}>
            {last.status === 'sent' && last.sent_at
              ? `Sent ${formatDateTimeInTz(last.sent_at, tz)} to ${last.email}${last.trigger === 'manual' && last.sent_by_name ? ` by ${last.sent_by_name}` : ''}`
              : last.status === 'failed' ? `Not sent (${last.attempts} ${last.attempts === 1 ? 'try' : 'tries'}): ${last.error || 'unknown'}` : 'Sending…'}
          </p>
        ) : null}
      </div>
    </li>
  );
}

function MonthlyStatements() {
  const { hasAccess: isAdmin } = useModuleLicense('accounts', 'admin');
  const [month, setMonth] = useState(lastMonth);
  const [data, setData] = useState<StatementPreview | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [picked, setPicked] = useState<Set<number>>(new Set());
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [results, setResults] = useState<Map<number, StatementSendResult>>(new Map());

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const res = await nurseryAccountsService.previewStatements(month);
      if (res.success && res.data) setData(res.data);
      else setError(errorMessage(res, 'The statements could not be worked out'));
    } catch (e) {
      setError(errorMessage(e, 'The statements could not be worked out'));
    }
    setLoading(false);
  }, [month]);

  useEffect(() => { setPicked(new Set()); setResults(new Map()); setNotice(null); load(); }, [load]);

  const toSend = useMemo(() => (data?.customers || []).filter((c) => c.send), [data]);
  const notSent = useMemo(() => (data?.customers || []).filter((c) => !c.send), [data]);
  const unsent = toSend.filter((c) => c.last_send?.status !== 'sent');
  const canSend = !!data?.settings.can_send;

  const send = async (opts: { customer_ids?: number[]; resend?: boolean; to_me?: boolean }) => {
    setBusy(true); setError(null); setNotice(null);
    try {
      const res = await nurseryAccountsService.sendStatements({ month, ...opts });
      if (res.success && res.data) {
        const r = res.data.results;
        setResults(new Map(r.map((x) => [x.customer_id, x])));
        const n = r.filter((x) => x.status === 'sent').length;
        const failed = r.filter((x) => x.status === 'failed').length;
        setNotice(opts.to_me
          ? `${n} ${n === 1 ? 'copy' : 'copies'} sent to you.`
          : `${n} sent${failed ? `, ${failed} not sent` : ''}.`);
        setPicked(new Set());
        if (!opts.to_me) await load();
      } else setError(errorMessage(res, 'The statements could not be sent'));
    } catch (e) {
      setError(errorMessage(e, 'The statements could not be sent'));
    }
    setBusy(false);
  };

  const pick = (id: number, v: boolean) => {
    const next = new Set(picked);
    if (v) next.add(id); else next.delete(id);
    setPicked(next);
  };
  const ids = [...picked];

  return (
    <>
      <PageHeader title="Monthly statements" subtitle="Each customer's statement for the month, emailed as a PDF"
        icon={<Mail className="h-6 w-6 text-white" />} />
      <div className="max-w-3xl mx-auto px-4 py-6 space-y-6">
        {error && <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm text-red-700 break-words">{error}</div>}
        {!isAdmin && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 text-sm text-amber-900 break-words">
            You hold the Accounts User seat. An Accounts Admin changes these settings and sends statements.
          </div>
        )}
        {data && !canSend && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 text-sm text-amber-900 flex gap-2 break-words">
            <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
            <span>This is a test server: its customers are a copy of real ones, so statements are not emailed to them from here. Choose up to five and send yourself a copy.</span>
          </div>
        )}

        <section className="bg-white border border-neutral-200 rounded-xl p-4 space-y-2 text-sm text-neutral-700 break-words">
          <p>
            Every customer that pays its own invoices is sent its statement: what it owed at the start of the month,
            each invoice, credit note and payment in it, and what it owes now, aged. A store its head office pays for
            is on the head office's statement.
          </p>
          <p>
            A customer gets statements when its invoices are emailed, unless you say otherwise on its account. They go
            to its statement email, else its email, from FactreeTech with replies to your billing email.
          </p>
        </section>

        {data && <SettingsCard settings={data.settings} isAdmin={isAdmin} onSaved={(s) => setData({ ...data, settings: s })} />}

        <section className="bg-white border border-neutral-200 rounded-xl p-4 space-y-3 min-w-0">
          <div className="flex flex-wrap items-end justify-between gap-3">
            <div className="space-y-1">
              <label htmlFor="stmt-month" className="block text-base font-semibold text-neutral-900">Month</label>
              <input id="stmt-month" type="month" value={month} onChange={(e) => e.target.value && setMonth(e.target.value)}
                className="rounded-lg border border-neutral-300 px-3 py-2 text-sm" />
            </div>
            {isAdmin && data && (
              <div className="flex flex-wrap gap-2">
                {canSend && (
                  <Button onClick={() => send({})} disabled={busy || unsent.length === 0}>
                    {busy ? 'Sending…' : `Send ${unsent.length} not yet sent`}
                  </Button>
                )}
                {canSend && ids.length > 0 && (
                  <Button variant="outline" onClick={() => send({ customer_ids: ids, resend: true })} disabled={busy}>
                    Send {ids.length} chosen again
                  </Button>
                )}
                <Button variant="outline" onClick={() => send({ customer_ids: ids, to_me: true })}
                  disabled={busy || ids.length === 0 || ids.length > 5}>
                  Send me a copy{ids.length ? ` (${ids.length})` : ''}
                </Button>
              </div>
            )}
          </div>
          {notice && <p className="text-sm text-green-800 break-words">{notice}</p>}
          {loading ? (
            <p className="text-sm text-neutral-500">Working out the statements…</p>
          ) : data && (
            <>
              <h3 className="text-sm font-semibold text-neutral-700">
                Sent a statement ({toSend.length})
              </h3>
              {toSend.length === 0
                ? <p className="text-sm text-neutral-500">Nobody for this month.</p>
                : (
                  <ul className="divide-y divide-neutral-100">
                    {toSend.map((c) => (
                      <CustomerRow key={c.customer_id} c={c} canPick={isAdmin} checked={picked.has(c.customer_id)}
                        onCheck={(v) => pick(c.customer_id, v)} result={results.get(c.customer_id)} />
                    ))}
                  </ul>
                )}
              {notSent.length > 0 && (
                <details className="text-sm">
                  <summary className="cursor-pointer text-neutral-600">Not sent one ({notSent.length})</summary>
                  <ul className="divide-y divide-neutral-100">
                    {notSent.map((c) => (
                      <CustomerRow key={c.customer_id} c={c} canPick={isAdmin} checked={picked.has(c.customer_id)}
                        onCheck={(v) => pick(c.customer_id, v)} result={results.get(c.customer_id)} />
                    ))}
                  </ul>
                </details>
              )}
            </>
          )}
        </section>
      </div>
    </>
  );
}

export default MonthlyStatements;
